import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DshService {
  private jsc: string | null = null;
  private hdim: string | null = null;
  private loading: Promise<boolean> | null = null;
  private activePollCancel: (() => void) | null = null;

  async loadDSH(maxAttempts = 60): Promise<boolean> {
    if (this.jsc && this.hdim) {
      return true;
    }

    if (!this.loading) {
      this.loading = this.waitForCallbacks(maxAttempts);
      const thisLoader = this.loading;
      thisLoader.then(() => { if (this.loading === thisLoader) { this.loading = null; } });
    }
    let deadlineTimer: any;
    const deadline = new Promise<boolean>(resolve => {
      deadlineTimer = setTimeout(() => resolve(!!(this.jsc && this.hdim)), maxAttempts * 1000);
    });
    return Promise.race([
      this.loading.then(result => { clearTimeout(deadlineTimer); return result; }),
      deadline
    ]);
  }

  private async waitForCallbacks(maxAttempts: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      let resolved = false;
      const timeout = maxAttempts * 1000;

      // hdim_payload_val is declared as "" in the inline script in index.html.
      // If it is undefined, that script was blocked (CSP nonce mismatch or load failure).
      try {
        const win = window as any;
        if (typeof win.hdim_payload_val === 'undefined') {
          console.error('[DSH] Initialisation script was blocked — hdim_payload_val is undefined. Check CSP nonce configuration.');
          try {
            const g = (window as any).gtag;
            if (typeof g === 'function') {
              g('event', 'dsh_script_csp_block', { 'event_category': 'dsh_error', 'event_label': 'hdim_inline_script_blocked' });
            }
          } catch (e) {}
          resolve(false);
          return;
        }
      } catch (e) {}

      const checkCallbackValues = () => {
        try {
          const win = window as any;
          if (win.hdim_payload_val && win.jsc_payload_val) {
            this.hdim = win.hdim_payload_val;
            this.jsc = win.jsc_payload_val;
            console.log('[DSH] Values loaded via callbacks');
            if (!resolved) {
              resolved = true;
              resolve(true);
            }
            return true;
          }
        } catch (error) {
          console.error('[DSH] Error accessing callback values:', error);
        }
        return false;
      };

      if (checkCallbackValues()) {
        return;
      }

      try {
        const win = window as any;
        if (win.dsh_callbacks && Array.isArray(win.dsh_callbacks)) {
          win.dsh_callbacks.push(() => {
            if (checkCallbackValues() && !resolved) {
              resolved = true;
              resolve(true);
            }
          });
        }
      } catch (error) {
        console.error('[DSH] Error registering callback:', error);
      }

      const TIMEOUT_GRACE_MS = 15000;
      let timeoutSeenAt: number | null = null;
      const startTime = Date.now();
      let pollInterval: any;
      const finish = (result: boolean) => {
        clearInterval(pollInterval);
        if (this.activePollCancel === cancel) {
          this.activePollCancel = null;
        }
        if (!resolved) {
          resolved = true;
          resolve(result);
        }
      };
      const cancel = () => finish(false);
      this.activePollCancel = cancel;

      pollInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;

        if (checkCallbackValues()) {
          finish(true);
          return;
        }

        try {
          const jscEl = document.getElementById('user_dsh') as HTMLInputElement;
          const hdimEl = document.getElementById('dsh_prefs2') as HTMLInputElement;

          if (jscEl?.value && hdimEl?.value) {
            this.jsc = jscEl.value;
            this.hdim = hdimEl.value;
            console.log(`[DSH] Loaded via DOM fallback after ${Math.floor(elapsed / 1000)}s`);
            finish(true);
            return;
          }
        } catch (error) {
          console.error('[DSH] Error during DOM polling:', error);
        }

        // dsh_timed_out is set by onInitializeTimeout / onResourceTimeout in index.html.
        // Keep polling for a grace window after it fires before giving up.
        try {
          if ((window as any).dsh_timed_out === true) {
            if (timeoutSeenAt === null) {
              timeoutSeenAt = Date.now();
              console.warn('[DSH] DSH library reported an external service timeout — continuing to poll for late values.');
            } else if (Date.now() - timeoutSeenAt >= TIMEOUT_GRACE_MS) {
              console.warn(`[DSH] No values ${Math.floor(TIMEOUT_GRACE_MS / 1000)}s after timeout — stopping poll.`);
              finish(false);
              return;
            }
          }
        } catch (e) { /* ignore */ }

        if (elapsed >= timeout) {
          console.error(`[DSH] Failed to load after ${maxAttempts}s`);
          finish(false);
        }
      }, 1000);
    });
  }

  getJsc(): string | null {
    return this.jsc;
  }

  getHdim(): string | null {
    return this.hdim;
  }

  getValues(): { jsc: string | null; hdim: string | null } {
    return { jsc: this.jsc, hdim: this.hdim };
  }

  hasValues(): boolean {
    return !!(this.jsc && this.hdim);
  }

  // Re-triggers the Experian DSH call by invoking xp() again.
  // Verified against dsh.js 8.1.2: each xp() call runs C(), which first calls H() to abort
  // and discard the previous run, then re-collects and re-fires onInitialize/onValidate.
  // Each call also issues a fresh HDIM XHR to globalsiteanalytics.com.
  retryInitialization(): void {
    try {
      const win = window as any;

      if (typeof win.xp !== 'function') {
        console.warn('[DSH] xp() not available — dsh.js script did not load.');
        return;
      }

      if (win.hdim_payload_val && win.jsc_payload_val) {
        return;
      }

      // If an HDIM request is still in flight, let it finish rather than re-firing xp(),
      // which calls H() and aborts the pending request. A later touchpoint will retry once
      // this attempt has settled (loadend clears dsh_hdim_inflight on success/error/timeout).
      if (win.dsh_hdim_inflight === true) {
        console.log('[DSH] HDIM request already in flight — not restarting.');
        return;
      }

      console.log('[DSH] Retrying DSH initialization...');

      // Stop any in-flight poller first so we don't leak a second concurrent setInterval.
      if (this.activePollCancel) {
        this.activePollCancel();
      }

      win.dsh_timed_out = false;
      win.dsh_initialized = false;
      win.dsh_callbacks = [];
      this.loading = null;

      win.xp({
        onInitialize: win.onDshInitialized,
        onValidate: win.onDshValidated,
        onInitializeTimeout: function () {
          console.warn('[DSH] HDIM initialization timed out on retry.');
          win.dsh_timed_out = true;
        },
        onResourceTimeout: function () {
          console.warn('[DSH] Resource load timed out on retry.');
          win.dsh_timed_out = true;
        }
      });
    } catch (error) {
      console.error('[DSH] Error retrying DSH initialization:', error);
    }
  }

  // Clears cached jsc/hdim on logout.
  // Intentionally preserves window.hdim_payload_val and window.jsc_payload_val —
  // dsh.js runs once per page load and cannot re-fire callbacks, so wiping these
  // would prevent recovery on re-login within the same SPA session.
  clearCache(): void {
    this.jsc = null;
    this.hdim = null;
    if (this.activePollCancel) {
      this.activePollCancel();
    }
    this.loading = null;

    try {
      (window as any).dsh_initialized = false;
      (window as any).dsh_timed_out = false;
    } catch (error) {
      console.error('[DSH] Error resetting DSH flags:', error);
    }
  }
}
